import { useState } from 'react';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { editPendingApproval, getPendingApprovals } from '../../../../api/endpoints/approvals';
import type { PendingApprovalItem, PendingApprovalProductType } from '../../../../api/types/approvals';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { DenyReasonDialog } from './DenyReasonDialog';

function approvalStatusColor(status?: string): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'Approved') return 'success';
  if (status === 'Denied') return 'error';
  if (status === 'Pending') return 'warning';
  return 'default';
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

function shortId(value?: string): string {
  if (!value) return '—';
  return value.length > 10 ? `${value.slice(0, 8)}…` : value;
}

/** One queue's rows — shared between the actionable ("waiting approval") table and the
 *  informational, read-only ("will approved") table. `actionable` toggles whether the
 *  Approve/Deny buttons render. */
function QueueSection({
  title,
  items,
  actionable,
  onApprove,
  onDeny,
  busyId,
}: {
  title: string;
  items: PendingApprovalItem[];
  actionable: boolean;
  onApprove: (item: PendingApprovalItem) => void;
  onDeny: (item: PendingApprovalItem) => void;
  busyId?: number;
}) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {title} ({items.length})
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Kayıt bulunmuyor.
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>No</TableCell>
                <TableCell>Sepet (ShoppingCartId)</TableCell>
                <TableCell>Çalışan</TableCell>
                <TableCell>Yönetici</TableCell>
                <TableCell>Onay Durumu</TableCell>
                <TableCell>Seviye</TableCell>
                <TableCell>Süresi Doldu</TableCell>
                <TableCell>Sebep</TableCell>
                <TableCell>Talep Tarihi</TableCell>
                {actionable && <TableCell align="right">İşlemler</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id ?? '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={item.shoppingCartId ?? ''}>
                      <span>{shortId(item.shoppingCartId)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{item.employeeId ?? '—'}</TableCell>
                  <TableCell>{item.managerID ?? '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={approvalStatusColor(item.approvalStatus)} label={item.approvalStatus ?? '—'} />
                  </TableCell>
                  <TableCell>
                    {typeof item.currentLevel === 'number' && typeof item.approvalLevelCount === 'number'
                      ? `${item.currentLevel} / ${item.approvalLevelCount}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {item.isExpired ? <Chip size="small" color="error" label="Evet" /> : <Chip size="small" label="Hayır" />}
                  </TableCell>
                  <TableCell>{item.reason || '—'}</TableCell>
                  <TableCell>{formatDate(item.insertedAt)}</TableCell>
                  {actionable && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={busyId === item.id}
                          onClick={() => onApprove(item)}
                        >
                          Onayla
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={busyId === item.id}
                          onClick={() => onDeny(item)}
                        >
                          Reddet
                        </Button>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

const PRODUCT_LABELS: Record<PendingApprovalProductType, string> = {
  Flight: 'uçak bileti',
  Hotel: 'otel',
  RentCar: 'araç kiralama',
  Transfer: 'transfer',
  Bus: 'otobüs bileti',
};

export function PendingApprovalTable({ productType }: { productType: PendingApprovalProductType }) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [approveTarget, setApproveTarget] = useState<PendingApprovalItem | null>(null);
  const [denyTarget, setDenyTarget] = useState<PendingApprovalItem | null>(null);

  const queryKey = ['pendingApprovals', productType];

  const queueQuery = useQuery({
    queryKey,
    queryFn: () => getPendingApprovals(productType),
  });

  const decisionMutation = useMutation({
    mutationFn: ({ item, approvalStatus, reason }: { item: PendingApprovalItem; approvalStatus: 'Approved' | 'Denied'; reason?: string }) =>
      editPendingApproval(productType, { id: item.id as number, approvalStatus, reason }),
    onSuccess: (_data, variables) => {
      enqueueSnackbar(
        variables.approvalStatus === 'Approved' ? 'Talep onaylandı.' : 'Talep reddedildi.',
        { variant: 'success' }
      );
      setApproveTarget(null);
      setDenyTarget(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' });
    },
  });

  const busyId = decisionMutation.isPending ? decisionMutation.variables?.item.id : undefined;

  return (
    <Box>
      {queueQuery.isLoading && <CircularProgress size={24} />}
      {queueQuery.isError && <ApiErrorAlert error={queueQuery.error} sx={{ mb: 2 }} />}

      {queueQuery.data && (
        <>
          <QueueSection
            title="Onayınızı Bekleyenler"
            items={queueQuery.data.waitingApproval}
            actionable
            onApprove={setApproveTarget}
            onDeny={setDenyTarget}
            busyId={busyId}
          />
          <QueueSection
            title="Diğer Onay Seviyesinde Bekleyenler"
            items={queueQuery.data.willApproved}
            actionable={false}
            onApprove={() => {}}
            onDeny={() => {}}
          />
        </>
      )}

      <ConfirmDialog
        open={!!approveTarget}
        title="Talebi onayla"
        description={
          approveTarget
            ? `#${approveTarget.id} numaralı ${PRODUCT_LABELS[productType]} onay talebini onaylamak istediğinize emin misiniz?`
            : undefined
        }
        confirmLabel="Onayla"
        loading={decisionMutation.isPending}
        onConfirm={() => approveTarget && decisionMutation.mutate({ item: approveTarget, approvalStatus: 'Approved' })}
        onCancel={() => setApproveTarget(null)}
      />

      <DenyReasonDialog
        open={!!denyTarget}
        description={
          denyTarget ? `#${denyTarget.id} numaralı ${PRODUCT_LABELS[productType]} onay talebini reddediyorsunuz.` : undefined
        }
        loading={decisionMutation.isPending}
        onConfirm={(reason) => denyTarget && decisionMutation.mutate({ item: denyTarget, approvalStatus: 'Denied', reason })}
        onCancel={() => setDenyTarget(null)}
      />
    </Box>
  );
}
